import { create } from 'zustand';

export const THEMES = {
  dark: 'Dark',
  light: 'Light',
} as const;

export type ThemeName = keyof typeof THEMES;

/** Dark is the CSS default — the arena is designed in it. */
export const DEFAULT_THEME: ThemeName = 'dark';

const STORAGE_KEY = 'chompdome-theme';

function isValidTheme(value: string): value is ThemeName {
  return value in THEMES;
}

function readStoredTheme(): ThemeName {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidTheme(stored)) return stored;
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_THEME;
}

function applyTheme(theme: ThemeName) {
  // 'dark' is the default in app.css, so it is expressed by the *absence* of data-theme.
  if (theme === DEFAULT_THEME) {
    delete document.documentElement.dataset.theme;
  } else {
    document.documentElement.dataset.theme = theme;
  }
}

interface ThemeState {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  toggleTheme: () => void;
}

// Applied on module load, before React renders, so there is no flash of the wrong palette.
const initialTheme = readStoredTheme();
applyTheme(initialTheme);

export const useThemeStore = create<ThemeState>()((set, get) => ({
  theme: initialTheme,
  setTheme: (theme) => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // localStorage unavailable
    }
    set({ theme });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
}));
